import { LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";

interface KPICardProps {
  title: string;
  count?: number;
  value?: string;
  icon: LucideIcon;
  variant?: "default" | "gold" | "teal" | "red" | "amber" | "peach" | "indigo";
  onClick?: () => void;
}

const styles = {
  default: {
    border: "border-slate-200",
    bg: "bg-gradient-to-br from-slate-50/80 to-white",
    icon: "bg-slate-100 text-slate-500",
  },
  amber: {
    border: "border-[#f4dec0]",
    bg: "bg-gradient-to-br from-[#fff9f1] to-white",
    icon: "bg-[#fdf2e2] text-[#7d94b2]",
  },
  gold: {
    border: "border-[#fde5bf]",
    bg: "bg-gradient-to-br from-[#fffaf2] to-white",
    icon: "bg-[#fff2df] text-[#f59e0b]",
  },
  teal: {
    border: "border-[#d7f0e8]",
    bg: "bg-gradient-to-br from-[#f5fcf9] to-white",
    icon: "bg-[#e8f8f1] text-[#10b981]",
  },
  peach: {
    border: "border-[#ffdcd1]",
    bg: "bg-gradient-to-br from-[#fff8f5] to-white",
    icon: "bg-[#fff0ea] text-[#f97316]",
  },
  indigo: {
    border: "border-[#dde2ff]",
    bg: "bg-gradient-to-br from-[#f8f9ff] to-white",
    icon: "bg-[#eef1ff] text-[#4f6bff]",
  },
  red: {
    border: "border-[#ffd6d6]",
    bg: "bg-gradient-to-br from-[#fff7f7] to-white",
    icon: "bg-[#ffecec] text-[#ef4444]",
  },
};

export const KPICard = ({
  title,
  count,
  value,
  icon: Icon,
  variant = "default",
  onClick,
}: KPICardProps) => {
  const style = styles[variant];

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative w-full overflow-hidden rounded-[18px] border text-left shadow-[0_1px_0_rgba(15,23,42,0.04)] transition-all duration-200",
        style.border,
        style.bg,
        onClick
          ? "hover:-translate-y-px focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#3f8b98]/10"
          : "cursor-default"
      )}
    >
      <div className="flex min-h-[92px] flex-col justify-between gap-2 px-5 py-3">
        <div className="pr-12">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8ea0b6]">
            {title}
          </p>
          {count !== undefined && (
            <div className="mt-2 text-[26px] font-semibold leading-none tracking-[-0.02em] text-slate-900">
              {count.toLocaleString()}
            </div>
          )}
        </div>
        {value && (
          <p className="pr-8 text-[12px] font-medium leading-[1.25] text-[#7286a3]">
            {value}
          </p>
        )}
        <div
          className={cn(
            "absolute right-4 top-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-transform duration-200 group-hover:scale-105",
            style.icon
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </button>
  );
};
